// src/StockMovementsReport.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE } from "./api";

const safeText = (v) => (v === null || v === undefined ? "" : String(v));
const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};
const toInt = (v) => {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) ? n : 0;
};
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};
const ymd = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function StockMovementsReport({ onBack }) {
  const today = useMemo(() => ymd(new Date()), []);
  const printRef = useRef(null);

  // filters
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState("cashier"); // cashier | type
  const [cashier, setCashier] = useState("");
  const [type, setType] = useState("all"); // all | sale | replenish | adjust
  const [q, setQ] = useState("");

  // data
  const [rows, setRows] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [types, setTypes] = useState([]);

  // ui
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const showMsg = (m) => {
    setMsg(m);
    window.clearTimeout(showMsg._t);
    showMsg._t = window.setTimeout(() => setMsg(""), 3500);
  };

  const load = async () => {
    setLoading(true);
    setMsg("");
    try {
      const params = {
        from,
        to,
        type: type || "all",
        cashier: cashier || "",
        q: q || "",
      };

      const res = await api.get("/server.php/reports/stock-movements", { params });

      if (res.data?.success) {
        setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
        setCashiers(Array.isArray(res.data.cashiers) ? res.data.cashiers : []);
        setTypes(Array.isArray(res.data.types) ? res.data.types : []);
        if (!res.data.rows || res.data.rows.length === 0) showMsg("No records in range.");
      } else {
        setRows([]);
        showMsg(res.data?.message || "Failed to load report");
      }
    } catch (e) {
      setRows([]);
      showMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived totals + summaries
  const totals = useMemo(() => {
    let totalIn = 0;
    let totalOut = 0;
    let net = 0;

    rows.forEach((r) => {
      const c = Number(r.qty_change ?? 0);
      if (c > 0) totalIn += c;
      if (c < 0) totalOut += Math.abs(c);
      net += c;
    });

    return { totalIn, totalOut, net };
  }, [rows]);

  const summary = useMemo(() => {
    // group by cashier OR by movement_type
    const map = new Map();

    rows.forEach((r) => {
      const key =
        groupBy === "type"
          ? (safeText(r.movement_type).toLowerCase() || "unknown")
          : (safeText(r.created_by) || "—");

      const qtyChange = Number(r.qty_change ?? 0);

      if (!map.has(key)) {
        map.set(key, {
          key,
          rows: 0,
          inQty: 0,
          outQty: 0,
          netQty: 0,
        });
      }
      const s = map.get(key);
      s.rows += 1;
      if (qtyChange > 0) s.inQty += qtyChange;
      if (qtyChange < 0) s.outQty += Math.abs(qtyChange);
      s.netQty += qtyChange;
    });

    const arr = Array.from(map.values());
    // sort: biggest activity first
    arr.sort((a, b) => Math.abs(b.netQty) - Math.abs(a.netQty));
    return arr;
  }, [rows, groupBy]);

  const onPrint = () => {
    // simple print that works with your current inline-styles
    window.print();
  };

  return (
    <div style={ui.page}>
      {/* print css */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <div style={ui.shell} ref={printRef}>
        {/* Header */}
        <div style={ui.topRow} className="no-print">
          <button style={ui.btn} onClick={onBack}>Back</button>

          <div style={ui.title}>Stock Movements Report</div>

          <div style={ui.topRight}>
            <button style={ui.btn} onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button style={ui.btn} onClick={onPrint}>Print</button>
          </div>
        </div>

        {/* Filters */}
        <div style={ui.filters} className="no-print">
          <div style={ui.filterRow}>
            <div style={ui.field}>
              <div style={ui.label}>From</div>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={ui.input} />
            </div>

            <div style={ui.field}>
              <div style={ui.label}>To</div>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={ui.input} />
            </div>

            <div style={ui.field}>
              <div style={ui.label}>Group By</div>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={ui.select}>
                <option value="cashier">Cashier</option>
                <option value="type">Movement Type</option>
              </select>
            </div>

            <div style={ui.field}>
              <div style={ui.label}>Cashier (optional)</div>
              <select value={cashier} onChange={(e) => setCashier(e.target.value)} style={ui.select}>
                <option value="">All</option>
                {cashiers.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div style={ui.field}>
              <div style={ui.label}>Type</div>
              <select value={type} onChange={(e) => setType(e.target.value)} style={ui.select}>
                <option value="all">All</option>
                {/* prefer server-provided list, but also keep common options */}
                {Array.from(new Set(["sale", "replenish", "adjust", ...types]))
                  .filter(Boolean)
                  .map((t) => (
                    <option key={t} value={t}>{String(t).toUpperCase()}</option>
                  ))}
              </select>
            </div>
          </div>

          <div style={ui.searchRow}>
            <div style={{ ...ui.field, flex: 1 }}>
              <div style={ui.label}>Search</div>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="item / sku / note / ref_id"
                style={{ ...ui.input, width: "100%" }}
              />
            </div>

            <button style={ui.btnPrimary} onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Search"}
            </button>
          </div>

          <div style={ui.apiHint}>
            API: <span style={ui.mono}>{API_BASE}</span>
          </div>
        </div>

        {/* message */}
        {msg ? <div style={ui.msg}>{msg}</div> : null}

        {/* Summary */}
        <div style={ui.block}>
          <div style={ui.blockTitle}>Summary ({groupBy === "type" ? "movement type" : "cashier"})</div>

          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={ui.th}>{groupBy === "type" ? "Type" : "Cashier"}</th>
                  <th style={ui.thRight}>Rows</th>
                  <th style={ui.thRight}>Total In</th>
                  <th style={ui.thRight}>Total Out</th>
                  <th style={ui.thRight}>Net</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 ? (
                  <tr>
                    <td style={ui.td} colSpan={5}>No summary rows.</td>
                  </tr>
                ) : (
                  summary.map((s) => (
                    <tr key={s.key}>
                      <td style={ui.td}>{safeText(s.key)}</td>
                      <td style={ui.tdRight}>{s.rows}</td>
                      <td style={ui.tdRight}>{toInt(s.inQty)}</td>
                      <td style={ui.tdRight}>{toInt(s.outQty)}</td>
                      <td style={ui.tdRight}>{toInt(s.netQty)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed */}
        <div style={ui.block}>
          <div style={ui.blockTitle}>Detailed Movements</div>

          <div style={ui.totalsLine}>
            Total In: <b>{toInt(totals.totalIn)}</b> &nbsp; | &nbsp;
            Total Out: <b>{toInt(totals.totalOut)}</b> &nbsp; | &nbsp;
            Net: <b>{toInt(totals.net)}</b> &nbsp; | &nbsp;
            Rows: <b>{rows.length}</b>
          </div>

          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: 180 }}>Date</th>
                  <th style={{ ...ui.th, width: 80 }}>Ref</th>
                  <th style={{ ...ui.th, width: 280 }}>Item</th>
                  <th style={{ ...ui.thRight, width: 80 }}>Before</th>
                  <th style={{ ...ui.thRight, width: 80 }}>Change</th>
                  <th style={{ ...ui.thRight, width: 80 }}>After</th>
                  <th style={{ ...ui.th, width: 120 }}>Type</th>
                  <th style={{ ...ui.th, width: 140 }}>Cashier</th>
                  <th style={{ ...ui.th, width: 220 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td style={ui.td} colSpan={9}>
                      {loading ? "Loading…" : "No movements in range."}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const ch = Number(r.qty_change ?? 0);
                    const chStyle = ch < 0 ? ui.neg : ch > 0 ? ui.pos : ui.neu;

                    const ref = r.ref_table
                      ? `${safeText(r.ref_table)}#${safeText(r.ref_id)}`
                      : safeText(r.ref_id) || "—";

                    return (
                      <tr key={r.id}>
                        <td style={ui.td}>{fmtDate(r.created_at)}</td>
                        <td style={ui.td}>{ref || "—"}</td>
                        <td style={ui.td}>
                          {safeText(r.item_name) || "—"}
                          {safeText(r.sku) ? <span style={ui.sku}>SKU: {safeText(r.sku)}</span> : null}
                        </td>
                        <td style={ui.tdRight}>{safeText(r.qty_before)}</td>
                        <td style={{ ...ui.tdRight, ...chStyle }}>{safeText(r.qty_change)}</td>
                        <td style={ui.tdRight}>{safeText(r.qty_after)}</td>
                        <td style={ui.td}>
                          <span style={badgeForType(r.movement_type)}>{safeText(r.movement_type).toUpperCase() || "—"}</span>
                        </td>
                        <td style={ui.td}>{safeText(r.created_by) || "—"}</td>
                        <td style={ui.td}>{safeText(r.note) || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={ui.footNote}>
            Tip: set <b>Type = REPLENISH</b> to view only replenishments saved in <code>stock_movements</code>.
          </div>
        </div>
      </div>
    </div>
  );
}

function badgeForType(t) {
  const x = String(t || "").toLowerCase();
  if (x === "sale") return { ...ui.badge, ...ui.badgeRed };
  if (x === "replenish") return { ...ui.badge, ...ui.badgeGreen };
  if (x === "adjust") return { ...ui.badge, ...ui.badgeBlue };
  return { ...ui.badge, ...ui.badgeGray };
}

/* -------------------- UI (matches your Sales Report style) -------------------- */
const ui = {
  page: { background: "#fff", minHeight: "100vh", padding: 18, color: "#111", fontFamily: "system-ui, Arial" },
  shell: { maxWidth: 1200, margin: "0 auto" },

  topRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 },
  title: { fontSize: 28, fontWeight: 900 },

  topRight: { display: "flex", gap: 10, alignItems: "center" },

  filters: { marginBottom: 10 },
  filterRow: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, alignItems: "end" },
  searchRow: { marginTop: 10, display: "flex", gap: 10, alignItems: "end" },

  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 700 },

  input: { padding: "10px 10px", borderRadius: 6, border: "1px solid #aaa", outline: "none" },
  select: { padding: "10px 10px", borderRadius: 6, border: "1px solid #aaa", outline: "none", background: "#fff" },

  btn: { padding: "6px 10px", border: "1px solid #777", borderRadius: 2, background: "#f5f5f5", cursor: "pointer" },
  btnPrimary: { padding: "10px 14px", border: "1px solid #222", borderRadius: 6, background: "#111", color: "#fff", cursor: "pointer", fontWeight: 800 },

  apiHint: { marginTop: 10, fontSize: 12, opacity: 0.75 },
  mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },

  msg: { color: "#b91c1c", fontWeight: 800, margin: "8px 0" },

  block: { marginTop: 14 },
  blockTitle: { fontSize: 20, fontWeight: 900, margin: "6px 0 10px" },

  totalsLine: { marginBottom: 8, fontSize: 14 },

  tableWrap: { borderTop: "1px solid #ddd", marginTop: 8 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #ddd", fontSize: 14, fontWeight: 900 },
  thRight: { textAlign: "right", padding: "10px 8px", borderBottom: "1px solid #ddd", fontSize: 14, fontWeight: 900 },
  td: { padding: "10px 8px", borderBottom: "1px solid #eee", fontSize: 14, verticalAlign: "top" },
  tdRight: { padding: "10px 8px", borderBottom: "1px solid #eee", fontSize: 14, textAlign: "right", verticalAlign: "top" },

  pos: { color: "#0f766e", fontWeight: 900 },
  neg: { color: "#b91c1c", fontWeight: 900 },
  neu: { color: "#111", fontWeight: 900 },

  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontWeight: 900, fontSize: 12, border: "1px solid transparent" },
  badgeRed: { background: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" },
  badgeGreen: { background: "#dcfce7", color: "#166534", borderColor: "#bbf7d0" },
  badgeBlue: { background: "#dbeafe", color: "#1d4ed8", borderColor: "#bfdbfe" },
  badgeGray: { background: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" },

  sku: { display: "block", fontSize: 12, opacity: 0.75, marginTop: 2 },
  footNote: { marginTop: 10, fontSize: 12, opacity: 0.8 },
};